// Quiz State Machine - Single source of truth for quiz state
import { PsychometricScores } from './psychometricScoring';

// Quiz phases as explicit enum
export type QuizPhase = 
  | 'intro'
  | 'questions'
  | 'psychometric'
  | 'generating'
  | 'celebration'
  | 'results'
  | 'error';

// The unified quiz state
export interface QuizState {
  phase: QuizPhase;
  userInterest: string;
  answers: string[];
  psychometricScores: PsychometricScores | null;
  identifiedTraits: string[];
  matchedSkills: string[];
  result: QuizResultData | null;
  error: string | null;
  createdAt: number;
  saveStatus: 'pending' | 'saving' | 'saved' | 'failed';
  /** In-quiz segment selection (school / college / working). */
  segment: 'school_student' | 'college_student' | 'working_professional' | null;
  /** Per-segment signals captured in chat (class, stream, branch, role, yoe…). */
  segmentSignals: Record<string, string | string[]>;
  /** Last role the user opened from results (for "resume where you left off"). */
  lastViewedRole?: { sectionId: string; id: string; title?: string; viewedAt?: number } | null;
  /** Last section the user opened (for the Hub "Last viewed" panel). */
  lastViewedSection?: { id: string; title?: string; viewedAt?: number } | null;
  /** Last in-app path the user viewed (career hub, role page, etc.). */
  lastViewedPath?: string | null;
  /** Query string captured with `lastViewedPath` (includes leading "?"). */
  lastViewedSearch?: string | null;
  /** URL hash captured with `lastViewedPath` (includes leading "#"). */
  lastViewedHash?: string | null;
  /** window.scrollY captured at the time `lastViewedPath` was recorded. */
  lastViewedScrollY?: number | null;
  /**
   * Per-segment snapshot of in-flight state so switching segment via
   * "Update career stage" doesn't blow away the previous segment's progress.
   * Keyed by segment string.
   */
  bySegment?: Partial<Record<'school_student' | 'college_student' | 'working_professional', {
    answers: string[];
    userInterest: string;
    segmentSignals: Record<string, string | string[]>;
    savedAt: number;
  }>>;
}

export interface QuizResultData {
  title: string;
  description: string;
  matchedRoles: MatchedRole[];
  sections: string[];
}

export interface MatchedRole {
  title: string;
  section: string;
  summary: string;
  sectionId: string;
  id: string;
  matchScore?: number;
  matchReason?: string;
  source: 'deterministic' | 'ai' | 'hybrid';
}

// Storage key
const QUIZ_STATE_KEY = 'quiz_state_v2';
const STATE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// Default state
export const getDefaultQuizState = (): QuizState => ({
  phase: 'intro',
  userInterest: '',
  answers: [],
  psychometricScores: null,
  identifiedTraits: [],
  matchedSkills: [],
  result: null,
  error: null,
  createdAt: Date.now(),
  saveStatus: 'pending',
  segment: null,
  segmentSignals: {},
  lastViewedRole: null,
  lastViewedPath: null,
  lastViewedSearch: null,
  lastViewedHash: null,
  lastViewedScrollY: null,
  bySegment: {},
});

// Persist state to sessionStorage
export const persistQuizState = (state: QuizState): void => {
  try {
    const stateToSave = {
      ...state,
      createdAt: state.createdAt || Date.now(),
    };
    sessionStorage.setItem(QUIZ_STATE_KEY, JSON.stringify(stateToSave));
    console.log('[QuizState] 💾 Persisted state:', state.phase);
  } catch (error) {
    console.error('[QuizState] Failed to persist state:', error);
  }
};

// Load state from sessionStorage
export const loadQuizState = (): QuizState | null => {
  try {
    const saved = sessionStorage.getItem(QUIZ_STATE_KEY);
    if (!saved) return null;

    const parsed = JSON.parse(saved) as QuizState;
    
    // Check TTL
    if (Date.now() - parsed.createdAt > STATE_TTL_MS) {
      console.log('[QuizState] ⏰ State expired, clearing');
      clearQuizState();
      return null;
    }

    console.log('[QuizState] 📥 Loaded state:', parsed.phase);
    return parsed;
  } catch (error) {
    console.error('[QuizState] Failed to load state:', error);
    return null;
  }
};

// Clear state
export const clearQuizState = (): void => {
  sessionStorage.removeItem(QUIZ_STATE_KEY);
  sessionStorage.removeItem('quiz-results'); // Legacy key
  sessionStorage.removeItem('from-quiz');
  console.log('[QuizState] 🗑️ Cleared state');
};

// Valid phase transitions
const validTransitions: Record<QuizPhase, QuizPhase[]> = {
  intro: ['questions', 'error'],
  questions: ['psychometric', 'generating', 'error'],
  psychometric: ['generating', 'questions', 'error'],
  generating: ['celebration', 'results', 'error'],
  celebration: ['results'],
  results: ['intro'], // Only explicit reset goes back to intro
  error: ['intro', 'questions'],
};

// Validate transition
export const canTransitionTo = (from: QuizPhase, to: QuizPhase): boolean => {
  return validTransitions[from]?.includes(to) ?? false;
};
